import React from "react";
import {
  FaGlobe,
  FaMobileAlt,
  FaApple,
  FaGooglePlay,
  FaDesktop,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { SiHuawei } from "react-icons/si";

const TradingPlatforms = () => {
  const buttonClass =
    "inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border-2 border-primary text-primary bg-transparent font-semibold transition-all duration-300 hover:bg-primary hover:text-white w-[160px] shrink-0";

  return (
    <section className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl p-8 text-text shadow-lg border border-gray-200 mb-8">
          <h1 className="text-4xl font-bold mb-3">
            XSiDER Trader XT5
          </h1>

          <p className="text-gray-600 max-w-3xl leading-7">
            Experience professional trading with the XSiDER platform.
            Access global financial markets using our secure Web Trader
            and Mobile Trading applications with real-time quotes,
            advanced charting, and seamless order execution.
          </p>
        </div>

        {/* Platform Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

          {/* Web Platform */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-2xl transition-all duration-300">

            <div className="bg-gray-50 border-b p-6 flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                <FaDesktop className="text-current text-3xl" />
              </div>

              <div>
                <h2 className="text-2xl font-bold text-text">
               XSiDER Trader XT5
                </h2>
                <p className="text-gray-500">
                 Web Browser Based Trading
                </p>
              </div>
            </div>

            <div className="p-6">
              <p className="text-gray-600 leading-7 mb-6">
                Trade directly from your browser without downloading
                any software. The XSiDER Web Platform provides
                lightning-fast execution, real-time market data,
                advanced charting tools, and secure account access.
              </p>

              <ul className="space-y-3 text-gray-700 mb-8">
                <li>✔ Real-time market quotes</li>
                <li>✔ Advanced interactive charts</li>
                <li>✔ One-click trading</li>
                <li>✔ Watchlists & market overview</li>
                <li>✔ Secure cloud-based access</li>
              </ul>

              <a
                href="https://webtrader.investain.com/"
                target="_blank"
                rel="noopener noreferrer"
                className={buttonClass}
              >
                <FaExternalLinkAlt />
                Web Trader
              </a>
            </div>
          </div>

          {/* Mobile Platform */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-2xl transition-all duration-300">

            <div className="bg-gray-50 border-b p-6 flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                <FaMobileAlt className="text-current text-3xl" />
              </div>

              <div>
                <h2 className="text-2xl font-bold text-text">
                  XSiDER Trader XT5
                </h2>
                <p className="text-gray-500">
                 Mobile app terminal
                </p>
              </div>
            </div>

            <div className="p-6">
              <p className="text-gray-600 leading-7 mb-6">
                Stay connected to the markets wherever you are.
                Download the XSiDER mobile application for iOS,
                Android, or Huawei devices and manage your trading
                account on the go.
              </p>

              <ul className="space-y-3 text-gray-700 mb-8">
                <li>✔ Live market prices</li>
                <li>✔ Instant trade execution</li>
                <li>✔ Push notifications</li>
                <li>✔ Secure biometric login</li>
                <li>✔ Full account management</li>
              </ul>

              <div className="flex flex-wrap gap-4">

                <a
                  href="https://apps.apple.com/ae/app/investain/id6475628015"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={buttonClass}
                >
                  <FaApple className="text-current text-lg" />
                  App Store
                </a>

                <a
                  href="https://play.google.com/store/apps/details?id=com.investain.investain&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={buttonClass}
                >
                  <FaGooglePlay className="text-current text-lg" />
                  Play Store
                </a>

                <a
                  href="https://appgallery.huawei.com/app/C114131343"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={buttonClass}
                >
                  <SiHuawei className="text-current text-lg" />
                  AppGallery
                </a>

              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="mt-10 bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
          <h2 className="text-2xl font-bold text-text mb-6">
            Why Choose XSiDER?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">

            <div className="p-5 rounded-xl border border-gray-200 hover:border-primary transition">
              <h3 className="font-semibold text-lg mb-2">
                Secure Trading
              </h3>
              <p className="text-gray-600 text-sm">
                Industry-standard encryption and secure authentication
                protect your trading account.
              </p>
            </div>

            <div className="p-5 rounded-xl border border-gray-200 hover:border-primary transition">
              <h3 className="font-semibold text-lg mb-2">
                Fast Execution
              </h3>
              <p className="text-gray-600 text-sm">
                Execute orders quickly with reliable and stable
                trading infrastructure.
              </p>
            </div>

            <div className="p-5 rounded-xl border border-gray-200 hover:border-primary transition">
              <h3 className="font-semibold text-lg mb-2">
                Advanced Charts
              </h3>
              <p className="text-gray-600 text-sm">
                Analyze markets using professional technical indicators
                and drawing tools.
              </p>
            </div>

            <div className="p-5 rounded-xl border border-gray-200 hover:border-primary transition">
              <h3 className="font-semibold text-lg mb-2">
                Multi Device Access
              </h3>
              <p className="text-gray-600 text-sm">
                Trade seamlessly across desktop, web, and mobile
                devices with synchronized accounts.
              </p>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default TradingPlatforms;
